import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/lib/languages";
import {
  buildLocalizedPublicPath as buildSeoLocalizedPublicPath,
  normalizePublicPath,
  PUBLIC_CANONICAL_PATHS,
  PUBLIC_ROUTE_ALIASES,
} from "@/lib/seo/public-seo";

export const PUBLIC_LOCALE_HEADER = "x-vidseeds-public-locale";
export const PUBLIC_ROUTE_HEADER = "x-vidseeds-public-route";
export const PUBLIC_CANONICAL_PATH_HEADER = "x-vidseeds-public-path";

const PUBLIC_ONLY_PATHS = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/auth/callback",
] as const;

const PUBLIC_ONLY_PREFIXES = ["/.well-known/"] as const;

function isExactOrPrefixed(pathname: string, basePath: string): boolean {
  if (basePath === "/") {
    return pathname === "/";
  }

  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

function resolvePublicAlias(pathname: string): string | null {
  return (
    PUBLIC_ROUTE_ALIASES[pathname as keyof typeof PUBLIC_ROUTE_ALIASES] ?? null
  );
}

function getSupportedLocaleFromSegment(
  pathname: string
): SupportedLanguage | null {
  const [, firstSegment] = pathname.split("/");
  if (
    firstSegment &&
    SUPPORTED_LANGUAGES.includes(firstSegment as SupportedLanguage)
  ) {
    return firstSegment as SupportedLanguage;
  }

  return null;
}

function isCanonicalPublicPath(pathname: string): boolean {
  return PUBLIC_CANONICAL_PATHS.some((basePath) =>
    isExactOrPrefixed(pathname, basePath)
  );
}

function isKnownPublicPath(pathname: string): boolean {
  return (
    isCanonicalPublicPath(pathname) || resolvePublicAlias(pathname) !== null
  );
}

export function isLocalizedPublicPath(pathname: string): boolean {
  const normalized = normalizePublicPath(pathname);
  const locale = getSupportedLocaleFromSegment(normalized);

  if (!locale) {
    return isKnownPublicPath(normalized);
  }

  const remainder =
    normalized === `/${locale}`
      ? "/"
      : normalizePublicPath(normalized.slice(locale.length + 1) || "/");

  return isKnownPublicPath(remainder);
}

export function isPubliclyAccessiblePath(pathname: string): boolean {
  const normalized = normalizePublicPath(pathname);
  if (isLocalizedPublicPath(normalized)) {
    return true;
  }

  if (
    PUBLIC_ONLY_PREFIXES.some(
      (basePath) => normalized === basePath || normalized.startsWith(basePath)
    )
  ) {
    return true;
  }

  return PUBLIC_ONLY_PATHS.some((basePath) =>
    isExactOrPrefixed(normalized, basePath)
  );
}

export function buildLocalizedPublicPath(
  canonicalPath: string,
  locale: SupportedLanguage
): string {
  return buildSeoLocalizedPublicPath(canonicalPath, locale);
}

export function resolvePublicPathLocale(pathname: string): {
  isPublicRoute: boolean;
  canonicalPath: string;
  locale: SupportedLanguage;
  redirectPath?: string;
} | null {
  const normalized = normalizePublicPath(pathname);
  const locale = getSupportedLocaleFromSegment(normalized);

  if (!locale) {
    if (!isKnownPublicPath(normalized)) {
      return null;
    }

    const canonicalPath = resolvePublicAlias(normalized) ?? normalized;

    return {
      isPublicRoute: true,
      canonicalPath,
      locale: "en",
      redirectPath: canonicalPath !== normalized ? canonicalPath : undefined,
    };
  }

  const remainder =
    normalized === `/${locale}`
      ? "/"
      : normalizePublicPath(normalized.slice(locale.length + 1) || "/");

  if (!isKnownPublicPath(remainder)) {
    return null;
  }

  const canonicalPath = resolvePublicAlias(remainder) ?? remainder;

  if (canonicalPath !== remainder) {
    return {
      isPublicRoute: true,
      canonicalPath,
      locale,
      redirectPath: buildLocalizedPublicPath(canonicalPath, locale),
    };
  }

  if (locale === "en") {
    return {
      isPublicRoute: true,
      canonicalPath,
      locale,
      redirectPath: canonicalPath,
    };
  }

  return {
    isPublicRoute: true,
    canonicalPath,
    locale,
  };
}

export function localizePublicPathname(
  pathname: string,
  locale: SupportedLanguage
): string {
  const resolved = resolvePublicPathLocale(pathname);
  if (!resolved) {
    return pathname;
  }

  return buildLocalizedPublicPath(resolved.canonicalPath, locale);
}
